import React, { useState, useCallback, useEffect } from 'react';
import { useCases } from '../data/mock';
import { getImageUrl } from '../lib/api';
import { Building2, BookOpen, Train, Sun, Wrench, Key, ChevronLeft, ChevronRight } from 'lucide-react';

const iconMap = {
  1: BookOpen,
  2: Building2,
  3: Train,
  4: Sun,
  5: Key,
  6: Wrench
};

const UseCasesSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCards, setVisibleCards] = useState(3);

  // Responsive: adjust visible cards based on screen width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, useCases.length - visibleCards);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= maxIndex) return 0;
      return prev + 1;
    });
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev <= 0) return maxIndex;
      return prev - 1;
    });
  }, [maxIndex]);

  // Auto-play effect
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const handleManualNav = (direction) => {
    setIsAutoPlaying(false);
    if (direction === 'next') nextSlide();
    else prevSlide();
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  return (
    <section 
      className="py-16 lg:py-32 bg-white" 
      id="use-cases"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8 lg:mb-12">
          <div className="mb-6 sm:mb-0">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-slate-900 mb-4">
              Risinājumi pēc pielietojuma
            </h2>
            <div className="w-16 lg:w-20 h-1 bg-slate-800" />
          </div>
          
          {/* Navigation arrows */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleManualNav('prev')}
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white"
              aria-label="Iepriekšējais"
            >
              <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>
            <button
              onClick={() => handleManualNav('next')}
              className="w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white"
              aria-label="Nākamais"
            >
              <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>
          </div>
        </div>

        {/* Carousel container */}
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ 
              transform: `translateX(-${currentIndex * (100 / visibleCards)}%)`,
            }}
          >
            {useCases.map((useCase) => {
              const Icon = iconMap[useCase.id] || Building2;
              return (
                <div
                  key={useCase.id}
                  className="px-2 sm:px-3 lg:px-4"
                  style={{ width: `${100 / visibleCards}%`, flexShrink: 0 }}
                >
                  <div className="group bg-slate-50 rounded-xl lg:rounded-2xl overflow-hidden hover:bg-white hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 border border-transparent hover:border-slate-100 h-full">
                    {/* Image */}
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      <img
                        src={getImageUrl(useCase.image)}
                        alt={useCase.altText}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 sm:p-5 lg:p-8">
                      <div className="flex items-start space-x-3 lg:space-x-4 mb-3 lg:mb-4">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-slate-800 rounded-lg lg:rounded-xl flex items-center justify-center group-hover:bg-slate-700 transition-colors flex-shrink-0">
                          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <h3 className="font-bold text-slate-900 text-base sm:text-lg lg:text-2xl leading-tight">
                          {useCase.title}
                        </h3>
                      </div>
                      <p className="text-slate-600 text-sm lg:text-lg leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center space-x-2 mt-6 lg:mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsAutoPlaying(false);
                setTimeout(() => setIsAutoPlaying(true), 8000);
              }}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-slate-800 w-6 lg:w-8' 
                  : 'bg-slate-300 hover:bg-slate-400 w-2'
              }`}
              aria-label={`Lapa ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
